import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { auth, db, appId, setAdminSession, clearAdminSession, isAdminSession, serverTimestamp } from "../firebase/firebaseConfig.jsx";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { getIdToken } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import {
  ADMIN_EMAIL_ALLOWLIST,
  canAccessAdminPortal,
  getDefaultMenuPermissionsForRole,
} from "../utils/adminPermissions";

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fcmToken, setFcmToken] = useState(null);
  const adminEmailAllowlist = useRef(ADMIN_EMAIL_ALLOWLIST);

  const login = useCallback(async (email, password) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("🚀 Admin login() called:", { email, password: '***' });
    }
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("📝 Setting admin session marker...");
      }
      setAdminSession();
      
      if (process.env.NODE_ENV === 'development') {
        console.log("🔥 Attempting Firebase authentication...");
      }
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (process.env.NODE_ENV === 'development') {
        console.log("✅ Firebase authentication successful:", user.uid);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log("📄 Querying user profile...");
      }
      const userDocRef = doc(db, `artifacts/${appId}/users`, user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData = null;
      const isAllowlistedEmail = adminEmailAllowlist.current.has(user.email?.toLowerCase?.());
      
      if (userDoc.exists()) {
        userData = userDoc.data();
        
        if (!canAccessAdminPortal(userData) && !isAllowlistedEmail) {
          if (process.env.NODE_ENV === 'development') {
            console.log("❌ No admin permissions, logging out");
          }
          await signOut(auth);
          return { success: false, error: 'Admin permissions required.' };
        }
        
        if (isAllowlistedEmail && !canAccessAdminPortal(userData)) {
          if (process.env.NODE_ENV === 'development') {
            console.log("🛠️ Allowlisted admin email detected, upgrading profile");
          }
          const updatedAdminData = {
            ...userData,
            uid: user.uid,
            email: user.email,
            role: 'admin',
            admin: true,
            menuPermissions: userData.menuPermissions || getDefaultMenuPermissionsForRole('admin'),
            updatedAt: serverTimestamp(),
          };
          await setDoc(userDocRef, updatedAdminData, { merge: true });
          userData = updatedAdminData;
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log("✅ Admin permissions confirmed");
        }
      } else {
        if (isAllowlistedEmail) {
          const newAdminProfile = {
            uid: user.uid,
            email: user.email,
            role: 'admin',
            admin: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          await setDoc(userDocRef, newAdminProfile, { merge: true });
          userData = newAdminProfile;
          if (process.env.NODE_ENV === 'development') {
            console.log("✅ Allowlisted admin profile created");
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log("❌ User document not found, logging out");
          }
          await signOut(auth);
          return { success: false, error: 'User information not found.' };
        }
      }
      
      setUser(user);
      setProfile(userData);
      if (process.env.NODE_ENV === 'development') {
        console.log("✅ Login completed");
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return { success: true, user };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("❌ Admin login failed:", error);
      }
      
      let errorMessage = 'Login failed.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Email not found.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email format.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Account is disabled.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many login attempts. Account temporarily blocked.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Please check your network connection.';
          break;
        default:
          errorMessage = error.message || 'Login failed.';
      }
      
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      clearAdminSession();
      setUser(null);
      setProfile(null);
      setFcmToken(null);
      return { success: true };
    } catch (error) {
      console.error("❌ Logout failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("❌ Password reset failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const updateUserPassword = useCallback(async (newPassword) => {
    try {
      await updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (error) {
      console.error("❌ Password update failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const updateUserProfile = useCallback(async (profileData) => {
    try {
      await updateProfile(auth.currentUser, profileData);
      await updateDoc(doc(db, `artifacts/${appId}/users`, auth.currentUser.uid), profileData);
      setProfile(prev => ({ ...prev, ...profileData }));
      return { success: true };
    } catch (error) {
      console.error("❌ Profile update failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  useEffect(() => {
    // 초기화를 지연시켜 모듈 로딩 순서 문제 방지
    let mounted = true;
    let unsubscribe = null;
    
    const initializeAuth = async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log("🔍 UserContext useEffect 시작");
        }
        
        const appType = localStorage.getItem('kairos-app-type');
        if (appType !== 'admin') {
          if (process.env.NODE_ENV === 'development') {
            console.log("🔍 Admin UserContext: appType is not 'admin'. Resetting to 'admin'");
          }
          setAdminSession();
        }

        // 프로필 캐시 키
        const PROFILE_CACHE_KEY = `kairos-admin-profile-cache`;
        
        // 오프라인 오류인지 확인하는 헬퍼 함수
        const isOfflineError = (error) => {
          if (!error) return false;
          const errorCode = error.code || '';
          const errorMessage = error.message || '';
          return (
            errorCode === 'unavailable' ||
            errorCode === 'failed-precondition' ||
            errorMessage.includes('offline') ||
            errorMessage.includes('network') ||
            errorMessage.includes('connection') ||
            errorMessage.includes('timeout')
          );
        };

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!mounted) return;
      if (process.env.NODE_ENV === 'development') {
        console.log("🔍 onAuthStateChanged 호출:", { user: !!user, uid: user?.uid });
      }
      
      if (user) {
        if (process.env.NODE_ENV === 'development') {
          console.log("🔍 사용자 인증됨, 프로필 로딩 시작");
        }
        const isAllowlistedEmail = adminEmailAllowlist.current.has(user.email?.toLowerCase?.());
        
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log("🔑 Custom Claims 확인 중...");
          }
          const idTokenResult = await getIdToken(user, true);
          const customClaims = idTokenResult.claims;
          if (process.env.NODE_ENV === 'development') {
            console.log("🔑 Custom Claims:", customClaims);
          }
          
          const userWithClaims = {
            ...user,
            customClaims: customClaims
          };
          setUser(userWithClaims);
        } catch (claimsError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn("⚠️ Custom Claims 확인 실패:", claimsError);
          }
          setUser(user);
        }
        
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log("📄 사용자 프로필 조회 중...");
          }
          const userDocRef = doc(db, `artifacts/${appId}/users`, user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            let userData = userDoc.data();
            if (isAllowlistedEmail && !canAccessAdminPortal(userData)) {
              if (process.env.NODE_ENV === 'development') {
                console.log("🛠️ Allowlisted admin email detected, upgrading profile");
              }
              const updatedAdminData = {
                ...userData,
                uid: user.uid,
                email: user.email,
                role: 'admin',
                admin: true,
                menuPermissions: userData.menuPermissions || getDefaultMenuPermissionsForRole('admin'),
                updatedAt: serverTimestamp(),
              };
              await setDoc(userDocRef, updatedAdminData, { merge: true });
              userData = updatedAdminData;
            }
            if (process.env.NODE_ENV === 'development') {
              console.log("📄 사용자 데이터:", {
                uid: userData.uid,
                email: userData.email,
                role: userData.role,
                admin: userData.admin
              });
            }
            setProfile(userData);
            // 프로필 캐시 저장
            try {
              localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
                data: userData,
                timestamp: Date.now(),
                uid: user.uid
              }));
            } catch (cacheError) {
              if (process.env.NODE_ENV === 'development') {
                console.warn("⚠️ 프로필 캐시 저장 실패:", cacheError);
              }
            }
            if (process.env.NODE_ENV === 'development') {
              console.log("✅ 프로필 설정 완료");
            }
          } else {
            if (isAllowlistedEmail) {
              const newAdminProfile = {
                uid: user.uid,
                email: user.email,
                role: 'admin',
                admin: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              };
              await setDoc(userDocRef, newAdminProfile, { merge: true });
              setProfile(newAdminProfile);
              if (process.env.NODE_ENV === 'development') {
                console.log("✅ Allowlisted admin profile created");
              }
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log("❌ 사용자 문서 없음");
              }
              setProfile(null);
            }
          }
          
          // Admin 웹 앱에서는 FCM 토큰이 필요 없음
          // 관리자는 브라우저에서 직접 작업하므로 푸시 알림 불필요
        } catch (error) {
          // 프로덕션에서는 오프라인 오류는 조용히 처리 (정상적인 동작)
          if (process.env.NODE_ENV === 'development') {
            console.error("❌ 사용자 프로필 로딩 실패:", error);
          }
          
          // 오프라인 오류인 경우 캐시된 프로필 사용
          if (isOfflineError(error)) {
            if (process.env.NODE_ENV === 'development') {
              console.warn("⚠️ 오프라인 상태 감지, 캐시된 프로필 확인 중...");
            }
            try {
              const cachedProfile = localStorage.getItem(PROFILE_CACHE_KEY);
              if (cachedProfile) {
                const cached = JSON.parse(cachedProfile);
                // 캐시가 같은 사용자이고 24시간 이내인 경우 사용
                if (cached.uid === user.uid && (Date.now() - cached.timestamp) < 24 * 60 * 60 * 1000) {
                  if (process.env.NODE_ENV === 'development') {
                    console.log("✅ 캐시된 프로필 사용:", cached.data);
                  }
                  setProfile(cached.data);
                  // 오프라인 모드 메시지는 개발 환경에서만 표시
                  if (process.env.NODE_ENV === 'development') {
                    console.log("⚠️ 오프라인 모드: 캐시된 프로필을 사용합니다. 연결이 복구되면 자동으로 업데이트됩니다.");
                  }
                } else {
                  if (process.env.NODE_ENV === 'development') {
                    console.warn("⚠️ 캐시된 프로필이 만료되었거나 다른 사용자입니다.");
                  }
                  setProfile(null);
                }
              } else {
                if (process.env.NODE_ENV === 'development') {
                  console.warn("⚠️ 캐시된 프로필이 없습니다.");
                }
                setProfile(null);
              }
            } catch (cacheError) {
              if (process.env.NODE_ENV === 'development') {
                console.error("❌ 캐시된 프로필 읽기 실패:", cacheError);
              }
              setProfile(null);
            }
          } else {
            // 오프라인 오류가 아닌 경우 (인증 오류 등) - 개발 환경에서만 로그
            if (process.env.NODE_ENV === 'development') {
              console.error("❌ 오프라인이 아닌 오류:", error);
            }
            setProfile(null);
          }
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log("🔍 사용자 인증 안됨, 상태 초기화");
        }
        setUser(null);
        setProfile(null);
        setFcmToken(null);
        // 로그아웃 시 캐시도 제거
        try {
          localStorage.removeItem(PROFILE_CACHE_KEY);
        } catch (e) {
          // 무시
        }
      }
      
          if (process.env.NODE_ENV === 'development') {
            console.log("🔍 로딩 완료");
          }
          if (mounted) {
            setLoading(false);
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("❌ UserContext 초기화 오류:", error);
        }
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // 초기화를 약간 지연시켜 모듈 로딩 완료 보장
    const initTimeout = setTimeout(() => {
      initializeAuth();
    }, 0);

    return () => {
      mounted = false;
      clearTimeout(initTimeout);
      if (unsubscribe) {
        if (process.env.NODE_ENV === 'development') {
          console.log("🔍 UserContext cleanup");
        }
        unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    profile,
    loading,
    fcmToken,
    login,
    logout,
    resetPassword,
    updateUserPassword,
    updateUserProfile,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserContext;